import React, { useEffect, useState } from "react";
import { Grid, Button, Typography, Box, Breadcrumbs, Link } from "@mui/material";
import Layout from "../../../layout/Layout";
import { proformaAPI } from "../../../config";
import axios from "axios";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useParams, useNavigate } from "react-router-dom";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { PDFDownloadLink } from "@react-pdf/renderer";
import ProformaGeneratePdf from "./pdf/ProformaGeneratePdf";

function ProformaOrderView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const userData = JSON.parse(localStorage.getItem("user") || "{}");

  const [loading, setLoading] = useState(false);
  const [proformaDetails, setProformaDetails] = useState(null);
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    axios({
      method: "POST",
      url: proformaAPI,
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + userData.token,
      },
      data: {
        intType: 2,
        proformaId: id,
      },
    })
      .then((res) => {
        if (res.data.success && res.data.result.length > 0) {
          setProformaDetails(res.data.result[0]);
          setItems(res.data.result[1] || []);
        }
      })
      .catch((err) => {
        console.error("Failed to load Proforma details:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, userData.token]);

  if (loading) {
    return (
      <Layout>
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography>Loading Proforma Details...</Typography>
        </Box>
      </Layout>
    );
  }

  if (!proformaDetails) {
    return (
      <Layout>
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography color="error">Proforma Order not found.</Typography>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ p: 3, background: "#f8fafc", minHeight: "100vh" }}>
        {/* Navigation & Header */}
        <Grid container spacing={3} alignItems="center" sx={{ mb: 4 }}>
          <Grid item xs={12} lg={6} md={6}>
            <Typography variant="h5" fontWeight="bold">
              Proforma Order View ({proformaDetails.proforma_no})
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Link underline="hover" color="inherit" href="/proforma-order/list">
                Proforma List
              </Link>
              <Typography color="text.primary">View</Typography>
            </Breadcrumbs>
          </Grid>
          <Grid item xs={12} lg={6} md={6} style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIosIcon />}
              sx={{ borderRadius: "8px" }}
            >
              Back
            </Button>
            <PDFDownloadLink
              document={
                <ProformaGeneratePdf
                  proformaDetails={proformaDetails}
                  items={items}
                />
              }
              fileName={`Proforma-${proformaDetails.proforma_no}.pdf`}
              style={{
                textDecoration: "none",
                padding: "10px 20px",
                color: "#fff",
                backgroundColor: "#2F58CD",
                borderRadius: 8,
                fontWeight: "bold",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {({ loading }) =>
                loading ? "Generating PDF..." : (
                  <>
                    <PictureAsPdfIcon fontSize="small" />
                    Download Proforma PDF
                  </>
                )
              }
            </PDFDownloadLink>
          </Grid>
        </Grid>

        {/* Onscreen Invoice Preview */}
        <Box sx={{ background: "#fff", p: 4, borderRadius: "16px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)", border: "1px solid #e2e8f0" }}>
          {/* Top Row: Title & Company Info */}
          <Grid container spacing={2} sx={{ borderBottom: "2px solid #f1f5f9", pb: 3, mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="h4" color="primary" fontWeight="bold">
                PROFORMA INVOICE
              </Typography>
              <Typography color="text.secondary" mt={1}>
                Proforma No: <strong>{proformaDetails.proforma_no}</strong>
              </Typography>
              <Typography color="text.secondary">
                Date: <strong>{proformaDetails.proforma_date ? proformaDetails.proforma_date.split("T")[0] : ""}</strong>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} textAlign={{ xs: "left", sm: "right" }}>
              <Typography variant="h6" fontWeight="bold">
                {proformaDetails.company_Name}
              </Typography>
              <Typography color="text.secondary">{proformaDetails.company_address}</Typography>
            </Grid>
          </Grid>

          {/* Billing & Project Info */}
          <Grid container spacing={4} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1" fontWeight="bold" color="text.secondary" gutterBottom>
                Client Name / Bill To:
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {proformaDetails.vendor_name}
              </Typography>
              <Typography color="text.secondary">{proformaDetails.vendor_street}</Typography>
              <Typography color="text.secondary">{proformaDetails.vendor_city} - {proformaDetails.vendor_pincode}</Typography>
              <Typography color="text.secondary">GST: {proformaDetails.vendor_gst}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1" fontWeight="bold" color="text.secondary" gutterBottom>
                Ship To / Project Site:
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {proformaDetails.project_name}
              </Typography>
              <Typography color="text.secondary">Site Contact: {proformaDetails.site_eng_contact}</Typography>
            </Grid>
          </Grid>

          {/* Items Table */}
          <Box sx={{ border: "1px solid #e2e8f0", borderRadius: "12px", overflow: "hidden", mb: 4 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
              <thead>
                <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                  <th style={{ padding: "12px", fontWeight: "bold" }}>S.No</th>
                  <th style={{ padding: "12px", fontWeight: "bold" }}>Item Name & Description</th>
                  <th style={{ padding: "12px", fontWeight: "bold", textAlign: "center" }}>Qty</th>
                  <th style={{ padding: "12px", fontWeight: "bold", textAlign: "center" }}>Unit</th>
                  <th style={{ padding: "12px", fontWeight: "bold", textAlign: "right" }}>Rate (₹)</th>
                  <th style={{ padding: "12px", fontWeight: "bold", textAlign: "right" }}>Total (₹)</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={idx} style={{ borderBottom: "1px solid #e2e8f0" }}>
                    <td style={{ padding: "12px" }}>{idx + 1}</td>
                    <td style={{ padding: "12px" }}>
                      <Typography fontWeight="bold">{item.item_name}</Typography>
                      <Typography variant="body2" color="text.secondary">{item.item_description}</Typography>
                    </td>
                    <td style={{ padding: "12px", textAlign: "center" }}>{item.quantity}</td>
                    <td style={{ padding: "12px", textAlign: "center" }}>{item.unit}</td>
                    <td style={{ padding: "12px", textAlign: "right" }}>₹{parseFloat(item.rate || 0).toLocaleString("en-IN")}</td>
                    <td style={{ padding: "12px", textAlign: "right" }}>₹{parseFloat(item.total || 0).toLocaleString("en-IN")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>

          {/* Summary Box */}
          <Grid container justifyContent="flex-end">
            <Grid item xs={12} sm={4}>
              <Box sx={{ p: 2, background: "#f8fafc", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography color="text.secondary">Subtotal:</Typography>
                  <Typography>₹{parseFloat(proformaDetails.total_without_gst || 0).toLocaleString("en-IN")}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography color="text.secondary">Packing Charges:</Typography>
                  <Typography>₹{parseFloat(proformaDetails.packing_charge || 0).toLocaleString("en-IN")}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={2} sx={{ borderBottom: "1px solid #e2e8f0", pb: 1 }}>
                  <Typography color="text.secondary">GST Split:</Typography>
                  <Typography>₹{parseFloat(parseFloat(proformaDetails.total_cgst || 0) + parseFloat(proformaDetails.total_sgst || 0) + parseFloat(proformaDetails.total_igst || 0)).toLocaleString("en-IN")}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="h6" fontWeight="bold">Grand Total:</Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary.main">
                    ₹{parseFloat(proformaDetails.total_with_gst || 0).toLocaleString("en-IN")}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          {/* Terms & Conditions */}
          <Box sx={{ mt: 4, pt: 3, borderTop: "1px dashed #e2e8f0" }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Terms & Conditions:
            </Typography>
            <Typography variant="body2" color="text.secondary" style={{ whiteSpace: "pre-line" }}>
              {proformaDetails.terms || "Standard payment terms apply."}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Layout>
  );
}

export default ProformaOrderView;
